// Полоса «деньги в полях за N дней»: линия и формула под ней.
//
// ## Какой вопрос закрывает
//
// Пульт подотчёта отвечает «сколько висит» и «у кого». Он не отвечает на третий вопрос,
// который и решает, надо ли что-то делать: подотчёт РАСТЁТ или тает. Четырнадцать миллионов,
// которые растут третью неделю, и четырнадцать миллионов, которые вчера были восемнадцатью, —
// это два разных положения дел и два разных решения.
//
// ## Почему линия, а не столбики
//
// Тридцать дней столбиками — это тридцать узких прямоугольников, между которыми глаз ищет
// разницу по высоте. Линия показывает НАПРАВЛЕНИЕ, а направление здесь и есть ответ. Точных
// чисел по дням линия не даёт и не должна: за ними идут в список операций.
//
// ## Что было непонятно в первом кадре и как исправлено
//
// Линия висела без единой подписи: не сказано, что слева, что справа и что вообще нарисовано.
// Число «+14 085 035,00 ₽» стояло отдельно от трёх слагаемых, из которых оно и получилось, —
// то есть ряд чисел не читался как формула, хотя ею и был. Теперь у линии подписаны концы и
// названо, что она рисует, а под ней стоит замкнутая цепочка «выдано минус возвращено минус
// принято расходом равно изменение» (`ui-views.md`, «числа в ряд человек читает как формулу»).
//
// Вид взят из набора AlignUI (`widget-total-expenses`): линия без точек и заливки, плоская —
// правило графиков.

import type { CashTrendOut } from '@/shared/api/endpoints/reports';
import { formatRubles } from '@/shared/money/format';
import { Metric, MetricRow } from '@/shared/ui/metric';
import SparkLineChart from '@/shared/ui/chart-spark-line';

export interface ДеньгиВПоляхProps {
  data: CashTrendOut;
}

export function ДеньгиВПолях({ data }: ДеньгиВПоляхProps) {
  const дней = data.items.length;
  // Линия рисует ОСТАТОК нарастающим итогом, а не выдачи по дням. Выдачи по дням — это
  // рваная пила из нулей и всплесков, по которой направление не читается вовсе: деньги
  // выдают раз в неделю, а тратят каждый день. Нарастающий итог отвечает ровно на вопрос
  // «в полях становится больше денег или меньше».
  let накоплено = 0;
  const линия = data.items.map((день) => {
    накоплено += день.issued - день.returned - день.spent;
    return { день: день.day, значение: накоплено };
  });
  const итог = накоплено;

  return (
    <div className='flex flex-col gap-4'>
      {/* Сказано, что нарисовано. Без этой строки линия — просто кривая: человек не знает,
          деньги это, люди или часы, и растёт она вверх к добру или к худу. */}
      <span className='text-paragraph-sm text-text-sub-600'>
        Сколько денег на руках у людей, день за днём
      </span>

      <div className='flex flex-col gap-1'>
        <div className='h-12 w-full'>
          <SparkLineChart data={линия} index='день' category='значение' height={48} />
        </div>
        {/* Концы линии подписаны. Слева и справа — не самоочевидно: на графике без оси
            человек одинаково часто читает и слева направо, и наоборот. */}
        <div className='flex items-baseline justify-between text-paragraph-xs text-text-sub-600'>
          <span>{дней} дней назад</span>
          <span>сегодня</span>
        </div>
      </div>

      {/* Замкнутая цепочка: три слагаемых и результат. Крупное число в ряду ровно одно —
          ответ, ради которого сюда смотрят.

          Ряд общий (`shared/ui/metric`), а не своя вёрстка: он сам переносит доли на вторую
          строку, когда карточка узкая. Своими четырьмя колонками результат упирался в правый
          край карточки на экране расхода — знак рубля стоял вплотную к рамке. */}
      <MetricRow columns={4}>
        <Metric label='Выдано' value={formatRubles(data.total_issued)} />
        <Metric label='− Возвращено' value={formatRubles(data.total_returned)} />
        <Metric label='− Принято расходом' value={formatRubles(data.total_spent)} />
        <Metric
          tone='важный'
          label={
            итог > 0 ? '= Стало больше на' : итог < 0 ? '= Стало меньше на' : '= Не изменилось'
          }
          // Знак у нуля не ставится: «+0,00 ₽» читается как рост на ноль, а это
          // «не изменилось». Плюс — это факт, а не украшение.
          value={`${итог > 0 ? '+' : ''}${formatRubles(итог)}`}
        />
      </MetricRow>

      {/* Что это значит человеческими словами. Само по себе «стало больше» ни о чём не
          говорит: рост подотчёта — это не выручка, а деньги, за которые ещё не отчитались. */}
      <p className='text-paragraph-xs text-text-sub-600'>
        {итог > 0
          ? 'Выдавали быстрее, чем отчитывались и возвращали: денег, за которые ещё не отчитались, стало больше.'
          : итог < 0
            ? 'Отчитывались и возвращали быстрее, чем выдавали: неподтверждённых денег стало меньше.'
            : 'Сколько выдали, столько и вернули с отчётами.'}
      </p>
    </div>
  );
}
